<script setup lang="ts">
import type { SurpriseBagTranslations } from '@pkg/types/surpriseBag';
import { VirtualList } from '@pkg/ui/components/ui/virtualList';
import { Loader2 } from 'lucide-vue-next';
import EmptyStateIcon from '@pkg/cms-icons/icons/Empty_State_Icon.svg';
import { THEME } from '@pkg/config/misc/theme';
import { substitutePlainTextVariables } from '@pkg/utilities/strings';
import { useBrandProfileCityBags } from '~/app/composables/useBrandProfileData';

const props = defineProps<{
  city: string;
  chainId: string;
  brandName: string;
  surpriseBagsListTitle?: string | null;
  emptyStateText?: string | null;
}>();

const { locale } = useI18n();

const surpriseBagsHeading = computed(() => {
  const raw = props.surpriseBagsListTitle?.trim();
  if (!raw) { return 'Surprise Bags'; }
  return substitutePlainTextVariables(raw, { brand: props.brandName });
});

const { data: locationFinderDocs } = useLazyFetch<
  Array<{
    staticTranslations?: SurpriseBagTranslations;
    ctaText?: string;
  }>
>('/api/sanity/location-finder', {
  query: { locale: locale.value as string },
});

const staticTranslations = computed(() => locationFinderDocs.value?.[0]?.staticTranslations);
const ctaText = computed(() => locationFinderDocs.value?.[0]?.ctaText || 'Load more');

const { data: cityBags, pending, loadMore, canLoadMore } = useBrandProfileCityBags(props.chainId, props.city);

</script>

<template>
  <section>
    <h2 class="s-detail__title__secondary">
      {{ surpriseBagsHeading }}
    </h2>

    <div v-if="pending && (!cityBags || cityBags.length === 0)" class="s-loading">
      <Loader2 />
    </div>

    <template v-else-if="cityBags && cityBags.length > 0">
      <VirtualList
        :items="cityBags"
        :static-translations="staticTranslations"
      />
      <div v-if="canLoadMore" class="w-full flex justify-center mt-[60px] mb-[40px]">
        <button class="s-load-more__cta" :disabled="pending" @click="loadMore">
          {{ ctaText }}
        </button>
      </div>
    </template>

    <section v-else :theme="THEME.APP" class="s-empty">
      <img class="s-empty__img" :src="EmptyStateIcon" alt="">
      <p class="s-empty__text">
        {{ emptyStateText || 'No surprise bags available' }}
      </p>
    </section>
  </section>
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.s-detail__title__secondary {
  @mixin h5-tenon;
  font-weight: bold;
  padding: theme('spacing.16') 0px;
  color: var(--theme-heading-color-app);
}

.s-loading {
  display: flex;
  justify-content: center;
  padding: theme('spacing.40') 0;
}

.s-load-more__cta {
  @mixin cta-app;
  width: fit-content;
  padding: theme('spacing.8') theme('spacing.20');

  &:hover {
    background-color: theme('colors.primary.20');
  }

  &:disabled {
    background-color: theme('colors.neutral.30');
    color: theme('colors.neutral.60');
    cursor: not-allowed;

    &:hover {
      background-color: theme('colors.neutral.30');
      color: theme('colors.neutral.60');
    }
  }
}

.s-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: theme('spacing.0') theme('spacing.32') theme('spacing.120') theme('spacing.32');

  &__text {
    padding-top: theme('spacing.20');
    color: theme('colors.neutral.70');
    width: 70%;
    text-align: center;
  }

  &__img {
    width: 170px;
    height: auto;
  }
}
</style>
